import { Injectable } from '@angular/core';
import { definePreset, usePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import {
  DEFAULT_PRIMARY_SHADES,
  DEFAULT_HELP_SHADES,
  PRIMARY_PALETTES,
  HELP_PALETTES,
  isValidHexColor,
  generatePrimaryShades
} from './theme-palettes';

const PRIMARY_PALETTE_STORAGE_KEY = 'ui_primary_palette';
const HELP_PALETTE_STORAGE_KEY = 'ui_help_palette';
const CUSTOM_PRIMARY_COLOR_KEY = 'ui_primary_custom_color';
const CUSTOM_HELP_COLOR_KEY = 'ui_help_custom_color';
const DARK_MODE_BACKOFFICE_KEY = 'ui_dark_mode_backoffice';

const DEFAULT_PRESET = definePreset(Lara, {
  semantic: { primary: DEFAULT_PRIMARY_SHADES },
  primitive: { purple: DEFAULT_HELP_SHADES }
});

@Injectable({ providedIn: 'root' })
export class BackofficeThemeService {

  /** Applique le thème backoffice (palettes enregistrées) — à appeler uniquement sur route /backoffice. */
  applyBackofficeThemeFromStorage(): void {
    const primaryId = localStorage.getItem(PRIMARY_PALETTE_STORAGE_KEY) || 'violet';
    const helpId = localStorage.getItem(HELP_PALETTE_STORAGE_KEY) || 'amber';
    const isDark = localStorage.getItem(DARK_MODE_BACKOFFICE_KEY) === 'true';

    let primaryShades = PRIMARY_PALETTES.find(p => p.id === primaryId)?.shades ?? DEFAULT_PRIMARY_SHADES;
    if (primaryId === 'custom') {
      const hex = localStorage.getItem(CUSTOM_PRIMARY_COLOR_KEY);
      if (hex && isValidHexColor(hex)) {
        primaryShades = generatePrimaryShades(hex);
      }
    }

    const helpPalette = HELP_PALETTES.find(p => p.id === helpId) ?? HELP_PALETTES[0];
    let helpShades = DEFAULT_HELP_SHADES;
    if (helpId === 'custom') {
      const hex = localStorage.getItem(CUSTOM_HELP_COLOR_KEY);
      if (hex && isValidHexColor(hex)) {
        helpShades = generatePrimaryShades(hex);
      }
    } else {
      helpShades = generatePrimaryShades(helpPalette.color);
    }

    const preset = definePreset(Lara, {
      semantic: { primary: primaryShades },
      primitive: { purple: helpShades }
    });
    usePreset(preset);
    document.documentElement.setAttribute('data-ui-theme', 'lara');

    const mainShade = isDark ? '300' : '500';
    document.documentElement.style.setProperty('--main-color', primaryShades[mainShade] ?? DEFAULT_PRIMARY_SHADES[mainShade]);
    document.documentElement.style.setProperty('--gd-help-bg', helpPalette.color);
    document.documentElement.style.setProperty('--gd-help-hover', helpPalette.hover);
    document.documentElement.style.setProperty('--gd-help-text', helpPalette.text);
  }

  /** Applique le preset par défaut — à appeler sur customer-side, acces-client, gestion. */
  applyDefaultTheme(): void {
    usePreset(DEFAULT_PRESET);
    document.documentElement.setAttribute('data-ui-theme', 'lara');
    document.documentElement.style.setProperty('--main-color', DEFAULT_PRIMARY_SHADES['500']);
    document.documentElement.style.setProperty('--gd-help-bg', DEFAULT_HELP_SHADES['500']);
    document.documentElement.style.setProperty('--gd-help-hover', '#d4ad12');
    document.documentElement.style.setProperty('--gd-help-text', '#ffffff');
  }
}
